export const MAX_SOURCE_IMAGE_BYTES = 25 * 1024 * 1024
export const TARGET_IMAGE_BYTES = 3 * 1024 * 1024
export const MAX_IMAGE_DIMENSION = 2560
export const IMAGE_UPLOAD_CONCURRENCY = 3

export type ImageUploadPhase = 'queued' | 'optimizing' | 'uploading' | 'done'

type QueueEntry<T> = {
  task: () => Promise<T>
  resolve: (value: T) => void
  reject: (reason?: any) => void
}

let activeUploads = 0
const uploadQueue: Array<QueueEntry<any>> = []

const drainQueue = () => {
  while (activeUploads < IMAGE_UPLOAD_CONCURRENCY && uploadQueue.length > 0) {
    const entry = uploadQueue.shift()
    if (!entry) return

    activeUploads += 1
    entry
      .task()
      .then(entry.resolve)
      .catch(entry.reject)
      .finally(() => {
        activeUploads -= 1
        drainQueue()
      })
  }
}

export const runImageUploadTask = <T>(
  task: () => Promise<T>,
  onPhase?: (phase: ImageUploadPhase) => void
) =>
  new Promise<T>((resolve, reject) => {
    onPhase?.('queued')
    uploadQueue.push({
      task: async () => {
        onPhase?.('optimizing')
        return task()
      },
      resolve,
      reject
    })
    drainQueue()
  })

const outputName = (name: string) => {
  const base = name.replace(/\.[^.]+$/, '') || 'imagem'
  return `${base}.webp`
}

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  quality: number
): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', quality))

const bitmapFromImageElement = async (file: File) => {
  const url = URL.createObjectURL(file)

  try {
    const image = new Image()
    image.decoding = 'async'
    image.src = url
    await image.decode()

    const canvas = document.createElement('canvas')
    canvas.width = image.naturalWidth
    canvas.height = image.naturalHeight
    const context = canvas.getContext('2d')
    if (!context) throw new Error('canvas_unavailable')
    context.drawImage(image, 0, 0)

    return {
      width: image.naturalWidth,
      height: image.naturalHeight,
      draw: (
        targetContext: CanvasRenderingContext2D,
        width: number,
        height: number
      ) => targetContext.drawImage(canvas, 0, 0, width, height),
      close: () => undefined
    }
  } finally {
    URL.revokeObjectURL(url)
  }
}

const decodeImage = async (file: File) => {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file)
    return {
      width: bitmap.width,
      height: bitmap.height,
      draw: (
        context: CanvasRenderingContext2D,
        width: number,
        height: number
      ) => context.drawImage(bitmap, 0, 0, width, height),
      close: () => bitmap.close()
    }
  }

  return bitmapFromImageElement(file)
}

export const optimizeImageForUpload = async (file: File): Promise<File> => {
  if (!file) throw new Error('Selecione uma imagem para continuar.')

  if (file.size > MAX_SOURCE_IMAGE_BYTES) {
    throw new Error('A imagem deve ter no máximo 25 MB.')
  }

  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
  const allowedExtensions = [
    'jpg',
    'jpeg',
    'png',
    'webp',
    'avif',
    'heic',
    'heif'
  ]

  if (!allowedExtensions.includes(extension)) {
    throw new Error('Use uma imagem JPG, JPEG, PNG, WebP, AVIF, HEIC ou HEIF.')
  }

  const shouldFallbackOriginal = ['heic', 'heif'].includes(extension)

  let decoded: Awaited<ReturnType<typeof decodeImage>>
  try {
    decoded = await decodeImage(file)
  } catch {
    if (shouldFallbackOriginal) return file
    throw new Error(
      'O arquivo está corrompido ou não contém uma imagem válida.'
    )
  }

  try {
    if (decoded.width <= 0 || decoded.height <= 0) {
      throw new Error(
        'O arquivo está corrompido ou não contém uma imagem válida.'
      )
    }

    const scale = Math.min(
      1,
      MAX_IMAGE_DIMENSION / Math.max(decoded.width, decoded.height)
    )
    let width = Math.max(1, Math.round(decoded.width * scale))
    let height = Math.max(1, Math.round(decoded.height * scale))

    const render = async (quality: number) => {
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const context = canvas.getContext('2d')
      if (!context) throw new Error('Não foi possível otimizar esta imagem.')
      decoded.draw(context, width, height)
      return canvasToBlob(canvas, quality)
    }

    let quality = 0.84
    let blob = await render(quality)

    while (blob && blob.size > TARGET_IMAGE_BYTES && quality > 0.62) {
      quality -= 0.06
      blob = await render(quality)
    }

    if (blob && blob.size > TARGET_IMAGE_BYTES) {
      const reduction = Math.sqrt(TARGET_IMAGE_BYTES / blob.size)
      width = Math.max(1, Math.round(width * Math.max(0.65, reduction)))
      height = Math.max(1, Math.round(height * Math.max(0.65, reduction)))
      blob = await render(Math.max(0.68, quality))
    }

    if (!blob) return file

    if (
      file.size <= TARGET_IMAGE_BYTES &&
      width === decoded.width &&
      height === decoded.height &&
      blob.size >= file.size
    ) {
      return file
    }

    return new File([blob], outputName(file.name), {
      type: 'image/webp',
      lastModified: file.lastModified
    })
  } finally {
    decoded.close()
  }
}
